import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrash, faMinus, faPlus, faShoppingBag, faTruck, faCheck, faTimes, faTag } from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../context/CartContext';
import { useProductTracking } from '../hooks/useProductTracking';
import api from '../config/api';
import parameterService from '../services/parameterService';
import CampaignCalculator from '../components/CampaignCalculator';
import './Cart.css';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, discount, applyDiscountCode, removeDiscountCode } = useCart();
  const { trackProductRemoveFromCart } = useProductTracking();
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingItem, setLoadingItem] = useState(null);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [isValidatingStock, setIsValidatingStock] = useState(false);
  const [shippingFee, setShippingFee] = useState(0);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(300);
  const [shippingEnabled, setShippingEnabled] = useState(true);
  
  // Kampanya state'i (component'ten gelecek)
  const [campaignData, setCampaignData] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (cart && cart.length > 0) {
      // Sadece sayfa ilk yüklendiğinde stok kontrolü yap
      // Miktar değişimlerinde bu fonksiyon tekrar çalışmasın
      const timer = setTimeout(() => {
        validateCartStock();
      }, 100); // Kısa delay ile sepet formatlanmasını bekle
      
      return () => clearTimeout(timer);
    } else {
      setOutOfStockItems([]);
    }
  }, [cart.length]); // Sadece sepet item sayısı değiştiğinde çalışsın



  // Kargo parametrelerini yükle
  useEffect(() => {
    const loadShippingParameters = async () => {
      try {
        // Cache'i temizle
        parameterService.clearCache();
        
        const fee = await parameterService.getParameter('shipping_fee', null);
        const threshold = await parameterService.getParameter('free_shipping_threshold', null);
        const enabled = await parameterService.getParameter('shipping_enabled', null);
        
        // Eğer parametreler null ise, API'den tekrar dene
        if (fee === null || threshold === null || enabled === null) {
          const response = await api.get('/parameters');
          if (response.data.success) {
            const shippingFeeParam = response.data.data.find(p => p.param_key === 'shipping_fee');
            const thresholdParam = response.data.data.find(p => p.param_key === 'free_shipping_threshold');
            const enabledParam = response.data.data.find(p => p.param_key === 'shipping_enabled');
            
            if (shippingFeeParam) {
              setShippingFee(parseFloat(shippingFeeParam.param_value));
            }
            if (thresholdParam) {
              setFreeShippingThreshold(parseFloat(thresholdParam.param_value));
            }
            // shipping_enabled parametresi API'den dönmüyorsa (pasif olduğu için) false olarak ayarla
            if (enabledParam) {
              setShippingEnabled(enabledParam.param_value === 'true');
            } else {
              setShippingEnabled(false); // API'den dönmüyorsa pasif kabul et
            }
          }
        } else {
          setShippingFee(fee);
          setFreeShippingThreshold(threshold);
          setShippingEnabled(enabled);
        }
      } catch (error) {
        console.error('Kargo parametreleri yüklenirken hata:', error);
        // Hata durumunda parametreleri yüklemeye devam et
        setShippingFee(null);
        setFreeShippingThreshold(null);
        setShippingEnabled(null);
      }
    };

    loadShippingParameters();
  }, []);



  const validateCartStock = async () => {
    if (cart.length === 0) return;
    
    setIsValidatingStock(true);
    const outOfStock = [];
    
    try {
      for (const item of cart) {
        const isCustom = item.is_custom;
        const productId = item.product_id || item.product.id;
        const modelId = item.selectedModel?.id || item.phone_model?.id || item.phone_model_id;
        const colorId = item.selectedColor?.id || item.color?.id || item.color_id || 0;
        
        // DETAYLI DEBUG: Sepet öğesinin tüm yapısını göster
        console.log('🔍 DETAYLI CART ITEM DEBUG:', {
          full_item: item,
          is_custom_field: item.is_custom,
          product_object: item.product,
          product_is_custom: item.product?.is_custom
        });
        
        try {
          const endpoint = isCustom 
            ? `/phone-models/${modelId}/colors/${colorId}/custom-design-stock?product_type_id=1`
            : `/products/${productId}/models/${modelId}/colors/${colorId}/stock`;
          
          console.log('🚀 Cart validateStock endpoint debug:', {
            product_id: productId,
            model_id: modelId,
            color_id: colorId,
            is_custom: isCustom,
            selected_endpoint: endpoint
          });
          
          const response = await api.get(endpoint);
          const availableStock = response.data.stock || 0;
          
          if (availableStock === 0 || item.quantity > availableStock) {
            outOfStock.push({
              id: item.id,
              product: item.product,
              model: item.selectedModel || item.phone_model,
              color: item.selectedColor || item.color,
              availableStock,
              requestedQuantity: item.quantity
            });
          }
        } catch (error) {
          console.error('Stok sorgulama hatası:', error);
          outOfStock.push({
            id: item.id,
            product: item.product,
            model: item.selectedModel || item.phone_model,
            color: item.selectedColor || item.color,
            availableStock: 0,
            requestedQuantity: item.quantity,
            error: true
          });
        }
      }
      
      setOutOfStockItems(outOfStock);
      
      if (outOfStock.length > 0) {
        setErrorMessage('Sepetinizdeki bazı ürünlerin stok durumu değişti. Lütfen kontrol edin.');
      } else {
        setErrorMessage('');
      }
    } catch (error) {
      console.error('Stok doğrulama hatası:', error);
    } finally {
      setIsValidatingStock(false);
    }
  };

  const handleProceedToCheckout = (e) => {
    if (outOfStockItems.length > 0) {
      e.preventDefault();
      setErrorMessage('Stokta olmayan ürünler var. Lütfen sepetinizi güncelleyin.');
      
      if (outOfStockItems.length > 0) {
        const firstItemId = outOfStockItems[0].id;
        const element = document.getElementById(`cart-item-${firstItemId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  const handleUpdateQuantity = async (productId, phoneModelId, colorId, newQuantity) => {
    if (newQuantity <= 0) return;
    
    // Yükleniyor durumunu göster
    setLoadingItem(`${productId}-${phoneModelId}-${colorId}`);
    setErrorMessage('');
    
    try {
      // Sepetteki ilgili ürünü bul
      const item = cart.find(item => {
        const itemProductId = Number(item.product_id || item.product.id);
        const itemModelId = Number(item.selectedModel?.id || item.phone_model?.id || item.phone_model_id);
        const itemColorId = Number(item.selectedColor?.id || item.color?.id || item.color_id || 0);
        
        return itemProductId === Number(productId) && 
               itemModelId === Number(phoneModelId) && 
               (itemColorId === Number(colorId) || (itemColorId === 0 && Number(colorId) === 0));
      });
      
      if (!item) {
        setErrorMessage('Ürün sepette bulunamadı.');
        return;
      }
      
      // Mevcut miktarla aynıysa işlem yapma
      if (item.quantity === newQuantity) {
        return;
      }
      
      // Özel tasarım mı kontrol et
      const isCustomDesign = item.is_custom || false;
      
      // DETAYLI DEBUG: updateQuantity için item yapısını göster
      console.log('🔍 DETAYLI UPDATE QUANTITY DEBUG:', {
        full_item: item,
        is_custom_field: item.is_custom,
        product_object: item.product,
        product_is_custom: item.product?.is_custom
      });
      
      // Stock kontrolü için API endpoint'i
      const endpoint = isCustomDesign
        ? `/phone-models/${phoneModelId}/colors/${colorId || 0}/custom-design-stock?product_type_id=1`
        : `/products/${productId}/models/${phoneModelId}/colors/${colorId || 0}/stock`;
      
      console.log('🚀 Cart updateQuantity endpoint debug:', {
        product_id: productId,
        phone_model_id: phoneModelId,
        color_id: colorId,
        is_custom: isCustomDesign,
        selected_endpoint: endpoint
      });
      
      // Stock durumunu kontrol et
      const stockResponse = await api.get(endpoint);
      const availableStock = stockResponse.data.stock || 0;
      
      // Stok yetersizse
      if (newQuantity > availableStock) {
        setErrorMessage(`Stok yetersiz! Bu ürün için maksimum ${availableStock} adet ekleyebilirsiniz. (Talep: ${newQuantity})`);
        
        // Eğer mevcut sepet miktarı stoktan fazlaysa ve stok sıfır değilse, stok miktarına güncelle
        if (item.quantity > availableStock && availableStock > 0) {
          await updateQuantity(productId, phoneModelId, colorId, availableStock);
        }
        return;
      }
      
      // Stok yeterliyse güncelle
      await updateQuantity(productId, phoneModelId, colorId, newQuantity);
      
    } catch (error) {
      console.error('Stok kontrolü veya miktar güncelleme hatası:', error);
      
      // Hatayı kullanıcıya göster
      if (error.response?.data?.error) {
        setErrorMessage(`İşlem hatası: ${error.response.data.error}`);
      } else {
        setErrorMessage('Stok durumu kontrol edilirken bir sorun oluştu. Lütfen sayfayı yenileyip tekrar deneyin.');
      }
    } finally {
      // Yükleniyor durumunu kaldır
      setLoadingItem(null);
    }
  };

  const handleRemoveItem = (productId, phoneModelId, colorId) => {
    // Tracking için product bilgisini al
    const cartItem = cart.find(item => 
      item.product_id === productId && 
      (item.phone_model_id || item.selectedModel?.id) === phoneModelId &&
      (item.color_id || item.selectedColor?.id) === colorId
    );
    
    if (cartItem) {
      const productForTracking = cartItem.product || { id: productId, name: cartItem.product_name || 'Ürün' };
      trackProductRemoveFromCart(productForTracking);
    }
    
    removeFromCart(productId, phoneModelId, colorId);
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const calculateShipping = async () => {
    try {
      const subtotal = calculateSubtotal();
      const fee = await parameterService.calculateShippingFee(subtotal);
      return fee;
    } catch (error) {
      console.error('Kargo ücreti hesaplanırken hata:', error);
      return null; // Hata durumunda null döndür
    }
  };

  // Senkron kargo hesaplama (display için)
  const getShippingFee = () => {
    const subtotal = calculateSubtotal();
    
    // Kargo pasifse veya parametreler yüklenmemişse kargo ücreti gösterilmez
    if (shippingEnabled === false || shippingFee === null || freeShippingThreshold === null) {
      return 0; // Kargo pasifse ücretsiz
    }
    
    return subtotal >= freeShippingThreshold ? 0 : shippingFee;
  };

  const calculateDiscount = () => {
    if (!discount) return 0;
    
    const subtotal = calculateSubtotal();
    return (subtotal * discount.percentage) / 100;
  };

  const calculateTotal = () => {
    const shipping = getShippingFee();
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscount();
    
    // Kampanya indirimi hesapla
    const campaignDiscount = campaignData && campaignData.campaignActive ? campaignData.savings : 0;
    
    // İndirim ve kampanya varsa subtotal'dan düşür
    const totalDiscount = discountAmount + campaignDiscount;
    const discountedSubtotal = subtotal - totalDiscount;
    
    return discountedSubtotal + (shipping !== null ? shipping : 0);
  };

  const handleApplyDiscountCode = () => {
    setDiscountError('');
    
    if (!discountCode.trim()) {
      setDiscountError('Lütfen bir indirim kodu girin.');
      return;
    }
    
    try {
      applyDiscountCode(discountCode);
      setDiscountCode('');
    } catch (error) {
      setDiscountError(error.message);
    }
  };

  const handleRemoveDiscount = () => {
    removeDiscountCode();
    setDiscountCode('');
  };

  if (!cart || cart.length === 0) {
    return (
      <div className="cart-page page-transition">
        <Container>
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <FontAwesomeIcon icon={faShoppingBag} />
            </div>
            <div className="empty-cart-content">
              <h2>Sepetiniz Şu An Boş</h2>
              <p>Sepetinizde ürün bulunmamaktadır. Hemen alışverişe başlayın!</p>
              <div className="empty-cart-actions">
                <Link to="/products" className="shop-now-button">
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Alışverişe Başla
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const subtotal = calculateSubtotal();
  const shipping = getShippingFee();
  const discountAmount = calculateDiscount();
  const total = calculateTotal();
  const remainingForFreeShipping = shippingEnabled && shipping !== null && shipping > 0 ? freeShippingThreshold - subtotal : 0;

  return (
    <div className="cart-page page-transition">
      <Container>
        <div className="cart-header">
          <h1 className="cart-title">Sepetim</h1>
          <Link to="/products" className="continue-shopping-link">
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Alışverişe Devam Et
          </Link>
        </div>
        
        {errorMessage && (
          <div className="stock-error-message alert alert-warning" role="alert">
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {errorMessage}
            <button type="button" className="btn-close" onClick={() => setErrorMessage('')} aria-label="Close"></button>
          </div>
        )}
        
        {isValidatingStock && (
          <div className="alert alert-info" role="alert">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
              Stok bilgileri kontrol ediliyor...
            </div>
          </div>
        )}
        
        <Row className="cart-content">
          <Col lg={8}>
            {cart.map((item) => (
              <div 
                id={`cart-item-${item.id}`} 
                key={`${item.product_id || item.product.id}-${item.selectedModel?.id || item.phone_model?.id}-${item.selectedColor?.id || item.color?.id}`}
                className={`cart-item ${outOfStockItems.some(oos => oos.id === item.id) ? 'out-of-stock-item' : ''}`}
              >
                <div className="cart-item-image">
                  <img src={item.product.images} alt={item.product.name} />
                </div>
                <div className="cart-item-info">
                  <h3 className="product-name">{item.product.name}</h3>
                  <div className="product-details">
                    {(item.selectedModel || item.phone_model) && (
                      <div className="model-info">
                        <span className="detail-label">Model:</span>
                        {item.selectedModel
                          ? `${item.selectedModel.brand} ${item.selectedModel.model_name}`
                          : `${item.phone_model.brand} ${item.phone_model.model_name}`}
                      </div>
                    )}
                    {(item.selectedColor || item.color) && (
                      <div className="color-info">
                        <span className="detail-label">Renk:</span>
                        <span className="color-display">
                          <span 
                            className="color-circle" 
                            style={{ 
                              backgroundColor: item.selectedColor?.code || item.color?.code,
                              border: (item.selectedColor?.code || item.color?.code) === '#FFFFFF' ? '1px solid #ddd' : 'none'
                            }}
                          ></span>
                          {item.selectedColor?.name || item.color?.name}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="price-tag">{(item.product.price).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL</div>
                </div>
                <div className="cart-item-controls">
                  <div className="quantity-controls">
                    <button 
                      onClick={() => handleUpdateQuantity(
                        item.product_id || item.product.id,
                        item.selectedModel?.id || item.phone_model?.id,
                        item.selectedColor?.id || item.color?.id,
                        item.quantity - 1
                      )}
                      className="quantity-btn"
                      disabled={item.quantity <= 1 || loadingItem === `${item.product_id || item.product.id}-${item.selectedModel?.id || item.phone_model?.id}-${item.selectedColor?.id || item.color?.id}`}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <span className="quantity">
                      {loadingItem === `${item.product_id || item.product.id}-${item.selectedModel?.id || item.phone_model?.id}-${item.selectedColor?.id || item.color?.id}` ? 
                        '...' : item.quantity}
                    </span>
                    <button 
                      onClick={() => handleUpdateQuantity(
                        item.product_id || item.product.id,
                        item.selectedModel?.id || item.phone_model?.id,
                        item.selectedColor?.id || item.color?.id,
                        item.quantity + 1
                      )}
                      className="quantity-btn"
                      disabled={loadingItem === `${item.product_id || item.product.id}-${item.selectedModel?.id || item.phone_model?.id}-${item.selectedColor?.id || item.color?.id}`}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  <button
                    onClick={() => handleRemoveItem(
                      item.product_id || item.product.id,
                      item.selectedModel?.id || item.phone_model?.id,
                      item.selectedColor?.id || item.color?.id
                    )}
                    className="remove-btn"
                    title="Ürünü Sil"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </Col>
          <Col lg={4}>
            <div className="cart-summary">
              <h2>Sipariş Özeti</h2>
              
              <div className="discount-code-section">
                <h5>
                  <FontAwesomeIcon icon={faTag} className="me-2" />
                  İndirim Kodu
                </h5>
                <div className="discount-code-input">
                  <Form.Control
                    type="text"
                    placeholder="İndirim kodunuzu girin"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    disabled={discount !== null}
                  />
                  <Button 
                    variant={discount ? "danger" : "brown"}
                    onClick={discount ? handleRemoveDiscount : handleApplyDiscountCode}
                    className={discount ? "" : "btn-brown"}
                  >
                    {discount ? (
                      <FontAwesomeIcon icon={faTimes} />
                    ) : (
                      "Uygula"
                    )}
                  </Button>
                </div>
                
                {discountError && (
                  <div className="discount-error">
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    {discountError}
                  </div>
                )}
                
                {discount && (
                  <div className="applied-discount">
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    <span>{discount.name} uygulandı!</span>
                  </div>
                )}
              </div>
              
              {/* Kampanya Bilgisi */}
              <CampaignCalculator 
                cartItems={cart}
                onCampaignCalculated={setCampaignData}
              />
              
              <div className="price-details">
                <div className="d-flex justify-content-between mb-2">
                  <span>Ara Toplam</span>
                  <span>{subtotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL</span>
                </div>
                
                {discount && (
                  <div className="d-flex justify-content-between mb-2 text-success">
                    <span>İndirim ({discount.percentage}%)</span>
                    <span>-{discountAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL</span>
                  </div>
                )}
                
                {campaignData && campaignData.campaignActive && campaignData.savings > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-success">
                    <span>Kampanya İndirimi</span>
                    <span>-{campaignData.savings.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL</span>
                  </div>
                )}
                
                <div className="d-flex justify-content-between mb-2">
                  <span>Kargo</span>
                  <span>
                    {shippingEnabled === null ? (
                      <span className="text-muted">Yükleniyor...</span>
                    ) : shippingEnabled === false ? (
                      <span className="text-success">Ücretsiz</span>
                    ) : (
                      shipping === 0 ? 'Ücretsiz' : `${shipping.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL`
                    )}
                  </span>
                </div>
                

                
                {shippingEnabled && shipping !== null && shipping > 0 && (
                  <div className="shipping-info">
                    <FontAwesomeIcon icon={faTruck} className="me-2" />
                    <span>{remainingForFreeShipping.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL daha ekleyin, kargo bedava!</span>
                  </div>
                )}
                
                {shippingEnabled && shipping === 0 && (
                  <div className="shipping-info text-success">
                    <FontAwesomeIcon icon={faTruck} className="me-2" />
                    <span>Kargo ücretsiz!</span>
                  </div>
                )}
                
                {shippingEnabled === false && (
                  <div className="shipping-info text-success">
                    <FontAwesomeIcon icon={faTruck} className="me-2" />
                    <span>Kargo ücretsiz!</span>
                  </div>
                )}
                
                <hr />
                
                <div className="d-flex justify-content-between total">
                  <span>Toplam</span>
                  <span>{total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} TL</span>
                </div>
              </div>
              
              <div className="cart-actions">
                {outOfStockItems.length > 0 && (
                  <div className="stock-warning mb-3">
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Sepetinizde stokta olmayan ürünler var. Ödeme adımına geçmek için lütfen sepetinizi güncelleyin.
                  </div>
                )}
                <Link 
                  to="/checkout" 
                  className={`checkout-button ${outOfStockItems.length > 0 ? 'disabled' : ''}`}
                  onClick={handleProceedToCheckout}
                >
                  {isValidatingStock ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Kontrol Ediliyor...
                    </>
                  ) : outOfStockItems.length > 0 ? (
                    'Stok Sorunu - Sepeti Güncelleyin'
                  ) : (
                    'Ödeme Adımına Geç'
                  )}
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Cart; 